import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Onboarding Queue page. One row per Active site whose onboarding_status
// is anything but 'Done'. This is the work queue for getting newly-added
// sites provisioned on xsiteio.
//
// To take a site OFF this list: either run the existing
// XsiteioOnboardingDialog from the parent customer's Sites tab (it
// flips onboarding_status to 'Done' once Step 4 succeeds), or open the
// site in edit mode and set onboarding_status to 'Done' manually.

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

// Tone map for the 10 milestone stages. Colors walk the progression
// warm -> cool -> green so a glance at the badge tells you roughly
// how far a site is. Grey fallback catches legacy or unexpected
// values so the badge never blows up on a bad row.
const STAGE_TONES = {
  'Not started':                   { bg: '#fee2e2', fg: '#b91c1c' },  // red
  'Client added':                  { bg: '#ffedd5', fg: '#9a3412' },  // light orange
  'Site configured':               { bg: '#fef3c7', fg: '#b45309' },  // amber
  'Bulloch config done & tested':  { bg: '#ede9fe', fg: '#5b21b6' },  // purple (POS-config)
  'BT900 Price Book imported':     { bg: '#e0e7ff', fg: '#4338ca' },  // indigo (POS content loaded)
  'Fuel Manager setup complete':   { bg: '#fae8ff', fg: '#a21caf' },  // fuchsia (fuel-side ready)
  'Site Ready email sent':         { bg: '#fce7f3', fg: '#be185d' },  // pink (outbound comm)
  'Training scheduled':            { bg: '#dbeafe', fg: '#1e40af' },  // blue
  'Training complete':             { bg: '#cffafe', fg: '#155e75' },  // teal
  'Done':                          { bg: '#d1fae5', fg: '#047857' },  // green
};

function StatusBadge({ value }) {
  const v = value || 'Not started';
  const tone = STAGE_TONES[v] || { bg: '#f3f4f6', fg: '#6b7280' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      background: tone.bg,
      color: tone.fg,
      borderRadius: 'var(--radius-sm, 4px)',
      fontFamily: 'var(--font-mono)',
      fontSize: '10.5px',
      fontWeight: 'var(--font-semibold)',
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    }}>{v}</span>
  );
}

export default function OnboardingQueue() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // Filter values match the enum strings verbatim ('Not started',
  // 'Client added', etc.) with 'all' as the reset. Done sites are
  // excluded from the queue upstream, so no 'Done' chip needed.
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('company');
  const [sortDir, setSortDir] = useState('asc');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/sites/needs-onboarding')
      .then((r) => { if (!cancelled) setSites(r.data?.sites || []); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // One count per stage the queue can hold (Done sites don't reach the
  // page, so no need to count them here).
  const QUEUE_STAGES = [
    'Not started',
    'Client added',
    'Site configured',
    'Bulloch config done & tested',
    'BT900 Price Book imported',
    'Fuel Manager setup complete',
    'Site Ready email sent',
    'Training scheduled',
    'Training complete',
  ];
  const counts = useMemo(() => {
    const c = { all: sites.length };
    for (const stage of QUEUE_STAGES) c[stage] = 0;
    for (const s of sites) {
      const v = s.onboarding_status || 'Not started';
      // Fold any legacy or unexpected value into 'Not started' for
      // counting purposes so the chips always sum to `all`.
      if (c[v] !== undefined) c[v]++;
      else c['Not started']++;
    }
    return c;
  }, [sites]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = sites;
    if (statusFilter !== 'all') {
      out = out.filter((s) => (s.onboarding_status || 'Not started') === statusFilter);
    }
    if (q) {
      out = out.filter((s) => {
        const hay = [
          s.company, s.first_name, s.last_name,
          s.site_number, s.site_name, s.city, s.province_state,
          s.fuel_brand, s.pos_type, s.xsite_io_username
        ].map((v) => v == null ? '' : String(v).toLowerCase()).join(' | ');
        return hay.includes(q);
      });
    }
    const sign = sortDir === 'desc' ? -1 : 1;
    return [...out].sort((a, b) => {
      const av = a[sortBy] ?? '';
      const bv = b[sortBy] ?? '';
      const aE = av === '' || av == null;
      const bE = bv === '' || bv == null;
      if (aE && bE) return 0;
      if (aE) return 1;
      if (bE) return -1;
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1 * sign;
      if (as > bs) return  1 * sign;
      return 0;
    });
  }, [sites, query, statusFilter, sortBy, sortDir]);

  function onHeader(key) {
    if (sortBy === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortBy(key); setSortDir('asc'); }
  }
  function arrow(key) {
    if (sortBy !== key) return '';
    return sortDir === 'desc' ? ' ▾' : ' ▴';
  }

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Onboarding Queue</h2>
          <div className="subtitle">
            Active sites that haven't been provisioned on xsiteio yet
            (onboarding_status &ne; 'Done'). Click a row to open the parent
            customer; use the xsiteio onboarding dialog from the Sites tab
            to walk Step 1 → Step 4.
          </div>
        </div>
      </div>

      {/* Status filter pills */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginBottom: 'var(--space-3)'
      }}>
        {[
          { key: 'all',                          label: 'All',                    count: counts.all,                                 tone: '#0e7490' },
          { key: 'Not started',                  label: 'Not started',            count: counts['Not started'],                      tone: '#b91c1c' },
          { key: 'Client added',                 label: 'Client added',           count: counts['Client added'],                     tone: '#9a3412' },
          { key: 'Site configured',              label: 'Site configured',        count: counts['Site configured'],                  tone: '#b45309' },
          { key: 'Bulloch config done & tested', label: 'Bulloch config',         count: counts['Bulloch config done & tested'],     tone: '#5b21b6' },
          { key: 'BT900 Price Book imported',    label: 'Price Book imported',    count: counts['BT900 Price Book imported'],        tone: '#4338ca' },
          { key: 'Fuel Manager setup complete',  label: 'FM setup done',          count: counts['Fuel Manager setup complete'],      tone: '#a21caf' },
          { key: 'Site Ready email sent',        label: 'Site Ready sent',        count: counts['Site Ready email sent'],            tone: '#be185d' },
          { key: 'Training scheduled',           label: 'Training scheduled',     count: counts['Training scheduled'],               tone: '#1e40af' },
          { key: 'Training complete',            label: 'Training complete',      count: counts['Training complete'],                tone: '#155e75' },
        ].map((f) => {
          const active = statusFilter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              style={{
                padding: '6px 14px',
                background: active ? f.tone : 'var(--color-surface, #fff)',
                color:      active ? '#fff' : 'var(--color-text)',
                border: `1px solid ${active ? f.tone : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-pill, 999px)',
                cursor: 'pointer',
                fontWeight: active ? 'var(--font-semibold)' : 'var(--font-regular)',
                fontSize: 'var(--text-small)'
              }}
            >
              {f.label} <span style={{ marginLeft: 6, fontFamily: 'var(--font-mono)' }}>{f.count}</span>
            </button>
          );
        })}
      </div>

      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        alignItems: 'center',
        marginBottom: 'var(--space-3)'
      }}>
        <input
          type="search"
          placeholder="Search company, site #, city, fuel brand…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ minWidth: 280, flex: '0 0 auto' }}
        />
        <span className="result-count">
          {loading ? 'Loading…' : `${visible.length} of ${sites.length} sites in queue`}
        </span>
      </div>

      {error && <p className="error">{error}</p>}
      {!loading && !error && sites.length === 0 && (
        <p className="panel-empty">Onboarding queue is empty — every Active site is marked Done.</p>
      )}

      {!loading && !error && sites.length > 0 && (
        <div className="panel" style={{ overflow: 'hidden', padding: 0 }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th onClick={() => onHeader('onboarding_status')} style={{ cursor: 'pointer', textAlign: 'left' }}>Status{arrow('onboarding_status')}</th>
                <th onClick={() => onHeader('company')}        style={{ cursor: 'pointer', textAlign: 'left' }}>Customer{arrow('company')}</th>
                <th onClick={() => onHeader('site_name')}      style={{ cursor: 'pointer', textAlign: 'left' }}>Site{arrow('site_name')}</th>
                <th onClick={() => onHeader('site_number')}    style={{ cursor: 'pointer', textAlign: 'left' }}>Site #{arrow('site_number')}</th>
                <th onClick={() => onHeader('city')}           style={{ cursor: 'pointer', textAlign: 'left' }}>City{arrow('city')}</th>
                <th onClick={() => onHeader('province_state')} style={{ cursor: 'pointer', textAlign: 'left' }}>Prov{arrow('province_state')}</th>
                <th onClick={() => onHeader('pos_type')}       style={{ cursor: 'pointer', textAlign: 'left' }}>POS{arrow('pos_type')}</th>
                <th style={{ textAlign: 'left' }}>xsiteio Login</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((s) => (
                <tr
                  key={s.site_id}
                  onClick={() => navigate(`/customers/${s.customer_id}`)}
                  style={{ cursor: 'pointer' }}
                  title={`Open ${s.company || 'customer'}`}
                >
                  <td><StatusBadge value={s.onboarding_status} /></td>
                  <td>
                    <div style={{ fontWeight: 'var(--font-semibold)' }}>{s.company || '—'}</div>
                    {(s.first_name || s.last_name) && (
                      <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
                        {[s.first_name, s.last_name].filter(Boolean).join(' ')}
                      </div>
                    )}
                  </td>
                  <td>{s.site_name || '—'}</td>
                  <td className="cell-mono">{s.site_number || '—'}</td>
                  <td>{s.city || '—'}</td>
                  <td>{s.province_state || '—'}</td>
                  <td>{s.pos_type || '—'}</td>
                  <td className="cell-mono" style={{ fontSize: 'var(--text-caption)' }}>
                    {s.xsite_io_username || <span style={{ color: 'var(--color-text-faint)' }}>not generated</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
